import '@availity/yup';
import * as yup from 'yup';
import { parseFollowUpDelays } from './followUpDelays';
import { aiModels } from '../../config/aiAgents';
import { QUIET_HOUR_PATTERN } from '../../config/aiFollowUpQuietHours';

const formValidation = yup.object().shape({
  name: yup.string().required(),
  channel: yup.string().oneOf(['whatsapp', 'call']).required(),
  objective: yup.string().required(),
  tone: yup.string(),

  // Needed on both routes: it fills the template's second variable and tells
  // the agent what it is called during the conversation.
  senderName: yup.string().when('channel', {
    is: 'whatsapp',
    then: () => yup.string().required(),
  }),

  openingTemplateName: yup.string().when(['channel', 'useDirectSend'], {
    is: (channel, useDirectSend) => channel === 'whatsapp' && !useDirectSend,
    then: () => yup.string().required(),
  }),
  // A template is sent in a language, and Meta refuses it without one. Left
  // optional, an operator could clear the default and save: the agent looks
  // configured and fails only when it opens its first conversation, on a real
  // lead, where nobody sees it but the logs.
  openingTemplateLanguage: yup.string().when(['channel', 'useDirectSend'], {
    is: (channel, useDirectSend) => channel === 'whatsapp' && !useDirectSend,
    then: () => yup.string().required(),
  }),
  useDirectSend: yup.boolean(),

  // The mirror of openingTemplateName: with direct send on, the template is
  // no longer needed but the text is. Without it startConversation refuses
  // the lead and it shows only in the logs.
  openingMessage: yup.string().when(['channel', 'useDirectSend'], {
    is: (channel, useDirectSend) => channel === 'whatsapp' && useDirectSend,
    then: () => yup.string().required(),
  }),

  collectFields: yup.array().of(
    yup.object().shape({
      leadFieldId: yup.string().required(),
      required: yup.boolean(),
      overwrite: yup.boolean(),
      hint: yup.string(),
    }),
  ),

  allowedOutcomes: yup.array().of(yup.string()),

  handoffWindowMinutes: yup.number().integer().positive().required(),
  maxTurns: yup.number().integer().positive().required(),

  // The delays are measured from the creation of the conversation: unless
  // they are shorter than the handoff window they never fire.
  followUpDelays: yup
    .string()
    .test(
      'followUpDelays',
      'Follow-up delays must be valid and shorter than the handoff window',
      function (value) {
        let delays;
        try {
          delays = parseFollowUpDelays(value);
        } catch (error) {
          return this.createError({ message: error.message });
        }

        const handoffWindow = this.parent.handoffWindowMinutes;
        if (!handoffWindow) return true;

        return delays.every((minutes) => minutes < handoffWindow);
      },
    ),

  // Both ends, each a time of day. The engine replaces a missing or malformed
  // end with the default band for both, silently: refusing the save here is
  // the only place the operator gets told. Equal ends are allowed, they mean
  // no band, and the form says so under the fields.
  quietHoursStart: yup
    .string()
    .matches(QUIET_HOUR_PATTERN, 'Must be a time as HH:mm')
    .required(),
  quietHoursEnd: yup
    .string()
    .matches(QUIET_HOUR_PATTERN, 'Must be a time as HH:mm')
    .required(),

  // A closed list: a model OpenRouter does not know would fail only at
  // runtime, inside a conversation already under way with the lead.
  model: yup
    .string()
    .oneOf(aiModels.map(({ value }) => value))
    .required(),

  bookliTaskWebhookUrl: yup.string().when('channel', {
    is: 'call',
    then: () =>
      yup
        .string()
        .url()
        .matches(/^https:\/\//, 'Must use https')
        .required(),
  }),
});

export default formValidation;
